import React from 'react';

import classNames from 'classnames';
import { faAlarmExclamation, faCalendar, faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Assignee from './assignee';
import Dropdown from 'components/dropdown';
import Parent from './parent';
import ReparentingMenuItems from 'bundles/crm/parents/reparenting_menu_items';
import Status from './status';
import TaskType from './task_type';
import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';

import type { Attachment, Task } from 'bundles/apex/tasks/types';
import type { MenuOptions } from 'bundles/apex/tasks/types';
import type { Parent as ParentType } from 'bundles/crm/types';

export interface Props {
  active?: boolean;
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  attachmentsPath: string;
  className?: string;
  currentParent?: ParentType;
  deleteTaskAttachment: any;
  features?: {
    dromoTaskImport?: boolean;
    marketingListings?: boolean;
    taskAttachmentExceptions?: boolean;
  };
  menuOptions?: MenuOptions;
  modalOpen?: boolean;
  showParents?: boolean | 'path';
  task: Task;
  onClick?: (task: Task) => void;
  onReparent?: (task: Task) => void;
  onUpdate: (task: Task) => Promise<Task>;
}

export default function TaskRow({
  active,
  addTaskAttachment,
  attachmentsPath,
  className,
  currentParent,
  deleteTaskAttachment,
  features,
  menuOptions,
  modalOpen,
  showParents,
  task,
  onClick,
  onReparent,
  onUpdate,
}: Props) {
  const overdue = !task.completed && task.deadline?.date && new Date(task.deadline.date) < new Date();
  const reparentOptions = menuOptions?.parents?.filter(
    option => option.parent.id !== task.parentId || option.parent.type !== task.parentType
  );

  return (
    <div
      className={classNames(
        'd-flex align-items-start gap-2 clickable py-2 px-1 border-bottom',
        { 'apex-tasks-list__task--active': active },
        className
      )}
      onClick={onClick && (() => onClick(task))}
    >
      <div onClick={e => e.stopPropagation()}>
        <Status
          addTaskAttachment={addTaskAttachment}
          attachmentsPath={attachmentsPath}
          deleteTaskAttachment={deleteTaskAttachment}
          features={features}
          task={task}
          onUpdate={onUpdate}
        />
      </div>
      <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-column gap-1 flex-grow-1" style={{ minWidth: 0 }}>
          <div className="text-truncate fw-bold">
            <Tooltip content={task.name} onlyOnOverflow>
              {task.name}
              {task.required &&
                <Tooltip content="Required for voucher submission">
                  <FontAwesomeIcon className="ms-1 text-danger fs-xs" icon={faAsterisk} />
                </Tooltip>
              }
            </Tooltip>
          </div>
          <div className="d-flex gap-2">
            {(task.deadline && task.deadline.date) && (
              <div
                className={classNames('d-flex gap-1 align-items-center', { 'text-solid-pink-900 fw-bold': overdue })}
              >
                {overdue
                  ? <FontAwesomeIcon icon={faAlarmExclamation} size="sm" />
                  : <FontAwesomeIcon icon={faCalendar} size="sm" />}
                {formatDate(task.deadline.date)}
              </div>
            )}
            {showParents &&
              <Parent
                currentParent={currentParent}
                modalOpen={modalOpen}
                task={task}
              />
            }
          </div>
        </div>

        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
          <Assignee assignee={task.assignee} />
          <TaskType task={task} />
        </div>
      </div>
      {!!reparentOptions?.length && (
        <div onClick={e => e.stopPropagation()}>
          <Dropdown icon={faEllipsisV}>
            <ReparentingMenuItems
              parentOptions={reparentOptions}
              record={task}
              onReparent={onReparent}
            />
          </Dropdown>
        </div>
      )}
    </div>
  );
}
